import { ReactNode, FC, memo } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Button, Icon } from '../components';
import { Icons, IconSize, ButtonColor, ButtonSize } from '../enums';

const StyledItemWrapper = styled.div<{ $isActive?: boolean; $isMobileLandscape?: boolean }>`
  ${tw`relative flex flex-col p-1 rounded-xl border sm:w-full md:w-[250px]`}
  ${({ $isActive }) =>
    $isActive ? tw`bg-white border-primary` : tw`border-gray-500 bg-gray-300`}

  &::before {
    ${tw`absolute top-0 left-0 w-full h-full rounded-xl`}
    ${({ $isActive }) => !$isActive && `bg-gray-400`}
  }
  overflow: hidden;
  ${({ $isMobileLandscape }) => $isMobileLandscape ? `
    height: 120px;
    min-height: 120px;
  ` : `
    height: 155px;
  `}
`;

const TopContainer = styled.div<{ $isMobileLandscape?: boolean }>`
  ${tw`flex items-center justify-between pl-2 pb-1 text-slate-500 rounded-t-2xl text-sm`};
  ${({ $isMobileLandscape }) => $isMobileLandscape ? `height: 24px;` : `height: 30px;`}
`;

const Heading = styled.p<{ $isMobileLandscape?: boolean }>`
  ${tw`flex-1 flex items-center gap-3 py-1 my-0 ml-1 mr-2 text-slate-500 leading-tight`};
  ${({ $isMobileLandscape }) => $isMobileLandscape ? tw`text-[10px] gap-1` : tw`text-xs gap-3`}
`;

const BottomContainer = styled.div<{ $noPadding?: boolean, $isActive?: boolean, $isMobileLandscape?: boolean }>`
  ${tw`h-full`};
  ${({ $isActive }) => (!$isActive && tw`mt-4`)}
  ${({ $noPadding }) => ($noPadding ? tw`px-2 pb-1` : tw`p-2 pb-2`)}
  ${({ $isMobileLandscape }) => $isMobileLandscape && tw`p-1 pb-1`}
`;

interface FilterItemWrapperProps {
  label: string;
  icon: Icons;
  noPadding?: boolean;
  isActive?: boolean;
  children: ReactNode;
  tooltip?: string;
  handleReset: () => void;
  isMobileLandscape?: boolean;
}

const FilterItemWrapper: FC<FilterItemWrapperProps> = ({
  label,
  icon,
  noPadding,
  isActive,
  children,
  tooltip,
  handleReset,
  isMobileLandscape,
}) => (
  <StyledItemWrapper key={label} $isActive={isActive} $isMobileLandscape={isMobileLandscape}>
    <TopContainer $isMobileLandscape={isMobileLandscape}>
      <Icon icon={icon} size={isMobileLandscape ? IconSize.small : IconSize.medium} />
      <Heading $isMobileLandscape={isMobileLandscape}>{label}
        </Heading>
        {tooltip && <Button toolTip={tooltip} size={ButtonSize.smallIcon} icon={Icons.question}/>}

      {isActive && (
        <Button
          className='z-1'
          onClick={handleReset}
          color={ButtonColor.transparent}
          size={isMobileLandscape ? ButtonSize.small : ButtonSize.medium}
          icon={Icons.trashcan}
        />
      )}
    </TopContainer>
    <BottomContainer $noPadding={noPadding} $isActive={isActive} $isMobileLandscape={isMobileLandscape}>{children}</BottomContainer>
  </StyledItemWrapper>
);

export default memo(FilterItemWrapper);
